import classNames from 'classnames'
import React, { useState, useEffect, useRef, ReactNode, useCallback, isValidElement, Children } from 'react'

import { getSessionStorage } from '@src/utils/enhancedSessionStorage'

import Icon from '../icon/Icon'

import styles from './dropdownV2.module.scss'
import Arrow from './icon/arrow.inline.svg'

export type Placement = 'bottom' | 'bottomLeft' | 'bottomRight' | 'top' | 'topLeft' | 'topRight'

export type Trigger = 'click' | 'hover' | 'contextMenu'

export interface MenuItem {
  key: string
  label: ReactNode
  disabled?: boolean
  onClick?: (e: React.MouseEvent) => void
  icon?: ReactNode
}

export interface DropdownProps {
  children: ReactNode // The trigger element that activates the dropdown (e.g., button or custom content)
  menu: {
    items: MenuItem[]
    onClick?: (item: MenuItem) => void // Callback when a menu item is clicked
    className?: string // Added className for the menu container
  }
  autoAdjustOverflow?: boolean // Whether to automatically adjust placement when off-screen
  disabled?: boolean
  destroyPopupOnHide?: boolean // Whether to destroy the dropdown DOM when hidden
  dropdownRender?: (menus: ReactNode) => ReactNode
  placement?: Placement
  trigger?: Trigger[]
  open?: boolean
  onOpenChange?: (open: boolean, info: { source: 'trigger' | 'menu' }) => void
  className?: string
  overlayClassName?: string
  buttonClassName?: string
  showArrow?: boolean
  customArrow?: ReactNode
  isActive?: boolean
  multiSelect?: boolean // Whether to allow selecting multiple items without closing the dropdown
  menuItemClassName?: string // Added className for all menu items
  onTriggerClick?: (e: React.MouseEvent, isOpen: boolean) => void // New prop for custom trigger click handling
  dropdownKey?: string
  forceClose?: boolean // Whether to force close the dropdown
}

const DROPDOWN_CLOSE_SOURCE_USER = 'user'
const DROPDOWN_CLOSE_SOURCE_OTHER = 'other'

const PLACEMENT_TOP = 'top'
const PLACEMENT_BOTTOM = 'bottom'
const PLACEMENT_RIGHT = 'Right'
const PLACEMENT_LEFT = 'Left'

export const DropdownV2: React.FC<DropdownProps> = ({
  children,
  menu,
  autoAdjustOverflow = true,
  disabled = false,
  destroyPopupOnHide = false,
  dropdownRender,
  placement = 'bottomLeft',
  trigger = ['hover'],
  open: controlledOpen,
  onOpenChange,
  className,
  overlayClassName,
  buttonClassName,
  showArrow = true,
  customArrow,
  isActive = false,
  multiSelect = false, // Default to false for backward compatibility
  menuItemClassName,
  onTriggerClick,
  dropdownKey,
  forceClose = false,
}) => {
  // Persistent open state logic
  const [isOpen, setIsOpen] = useState(() => {
    if (multiSelect && dropdownKey && typeof window !== 'undefined') {
      try {
        const stored = getSessionStorage().getItem<string>(`dropdownV2-open-${dropdownKey}`)
        if (stored) {
          const parsed = JSON.parse(stored)
          // Only auto-open if last close was NOT user-initiated
          if (parsed.open && parsed.closeSource !== DROPDOWN_CLOSE_SOURCE_USER) {
            return true
          }
        }
      } catch {
        /* ignore */
      }
    }
    return false
  })
  const [adjustedPlacement, setAdjustedPlacement] = useState(placement)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLDivElement>(null)
  const mousePositionRef = useRef<{ x: number; y: number } | null>(null)

  const isControlled = controlledOpen !== undefined
  const open = isControlled ? controlledOpen : isOpen

  // Effect to handle force close
  useEffect(() => {
    if (forceClose && isOpen) {
      setIsOpen(false)
      if (onOpenChange) {
        onOpenChange(false, { source: 'trigger' })
      }
      // Add session storage update when force closing
      if (multiSelect && dropdownKey && typeof window !== 'undefined') {
        try {
          getSessionStorage().setItem(
            `dropdownV2-open-${dropdownKey}`,
            JSON.stringify({ open: false, closeSource: DROPDOWN_CLOSE_SOURCE_USER }),
          )
        } catch {
          /* ignore */
        }
      }
    }
  }, [forceClose]) // Only run when forceClose changes, not on every prop change

  // Helper to persist open state
  const persistOpenState = useCallback(
    (open: boolean, closeSource: typeof DROPDOWN_CLOSE_SOURCE_USER | typeof DROPDOWN_CLOSE_SOURCE_OTHER) => {
      if (multiSelect && dropdownKey && typeof window !== 'undefined') {
        try {
          getSessionStorage().setItem(`dropdownV2-open-${dropdownKey}`, JSON.stringify({ open, closeSource }))
        } catch {
          /* ignore */
        }
      }
    },
    [dropdownKey, multiSelect],
  )

  const adjustPosition = useCallback(() => {
    if (!dropdownRef.current || !triggerRef.current) return

    // Only adjust if autoAdjustOverflow is true
    if (!autoAdjustOverflow) {
      setAdjustedPlacement(placement)
      return
    }

    const triggerRect = triggerRef.current.getBoundingClientRect()
    const dropdownRect = dropdownRef.current.getBoundingClientRect()
    const viewportHeight = window.innerHeight
    const viewportWidth = window.innerWidth

    let newPlacement = placement

    // Adjust vertical placement
    if (placement.includes(PLACEMENT_BOTTOM) && triggerRect.bottom + dropdownRect.height > viewportHeight) {
      newPlacement = placement.replace(PLACEMENT_BOTTOM, PLACEMENT_TOP) as Placement
    } else if (placement.includes(PLACEMENT_TOP) && triggerRect.top - dropdownRect.height < 0) {
      newPlacement = placement.replace(PLACEMENT_TOP, PLACEMENT_BOTTOM) as Placement
    }

    // Adjust horizontal placement
    if (placement.includes(PLACEMENT_RIGHT) && triggerRect.right + dropdownRect.width > viewportWidth) {
      newPlacement = newPlacement.replace(PLACEMENT_RIGHT, PLACEMENT_LEFT) as Placement
    } else if (placement.includes(PLACEMENT_LEFT) && triggerRect.left - dropdownRect.width < 0) {
      newPlacement = newPlacement.replace(PLACEMENT_LEFT, PLACEMENT_RIGHT) as Placement
    }

    setAdjustedPlacement(newPlacement)
  }, [placement, autoAdjustOverflow])

  useEffect(() => {
    if (open && autoAdjustOverflow) {
      adjustPosition()
    }
  }, [open, autoAdjustOverflow, adjustPosition])

  useEffect(() => {
    if (open && trigger.includes('hover')) {
      const handleMouseMove = (e: MouseEvent) => {
        mousePositionRef.current = { x: e.clientX, y: e.clientY }
      }
      document.addEventListener('mousemove', handleMouseMove)
      return () => {
        document.removeEventListener('mousemove', handleMouseMove)
      }
    }

    return undefined
  }, [open, trigger])

  const handleOpenChange = useCallback(
    (newOpen: boolean, source: 'trigger' | 'menu' | typeof DROPDOWN_CLOSE_SOURCE_OTHER) => {
      // If we're in multiSelect mode and trying to close from a menu click, don't close
      if (multiSelect && !newOpen && source === 'menu') {
        return
      }
      // Persist open state
      if (dropdownKey) {
        // If closing from trigger or outside, treat as user close
        const closeSource =
          !newOpen && (source === 'trigger' || source === DROPDOWN_CLOSE_SOURCE_OTHER)
            ? DROPDOWN_CLOSE_SOURCE_USER
            : DROPDOWN_CLOSE_SOURCE_OTHER
        persistOpenState(newOpen, closeSource)
      }
      if (isControlled) {
        onOpenChange?.(newOpen, { source: source === DROPDOWN_CLOSE_SOURCE_OTHER ? 'trigger' : source })
      } else {
        setIsOpen(newOpen)
      }
    },
    [multiSelect, isControlled, onOpenChange, dropdownKey, persistOpenState],
  )

  const handleClickOutside = useCallback(
    (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target as Node)
      ) {
        handleOpenChange(false, DROPDOWN_CLOSE_SOURCE_OTHER) // treat as user close
      }
    },
    [handleOpenChange],
  )

  useEffect(() => {
    if (open) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [open, handleClickOutside])

  const handleMenuItemClick = useCallback(
    (e: React.MouseEvent, item: MenuItem) => {
      // Stop propagation to prevent the dropdown from closing
      e.stopPropagation()

      if (!item.disabled) {
        item.onClick?.(e)
        menu.onClick?.(item)

        // Only close the dropdown if not in multiSelect mode
        if (!multiSelect) {
          handleOpenChange(false, 'menu')
        }
      }
    },
    [multiSelect, menu.onClick, handleOpenChange],
  )

  const getTriggerProps = () => {
    const props: React.HTMLAttributes<HTMLDivElement> = {}

    if (trigger.includes('click')) {
      props.onClick = e => {
        if (onTriggerClick) {
          onTriggerClick(e, !open)
        }
        handleOpenChange(!open, 'trigger')
      }
    }
    if (trigger.includes('hover')) {
      props.onMouseEnter = () => {
        handleOpenChange(true, 'trigger')
      }
      props.onMouseLeave = e => {
        // Only close if mouse is not moving toward the dropdown
        if (dropdownRef.current) {
          const dropdownRect = dropdownRef.current.getBoundingClientRect()

          // Store the mouse position when leaving the trigger
          mousePositionRef.current = { x: e.clientX, y: e.clientY }

          // Check if mouse is moving toward dropdown
          const isMovingToward = () => {
            if (!mousePositionRef.current) return false

            const currentPos = mousePositionRef.current
            const mouseY = currentPos.y

            // Determine if mouse is in the general direction of the dropdown
            // based on the placement
            if (adjustedPlacement.includes(PLACEMENT_BOTTOM)) {
              return mouseY <= dropdownRect.top + 5 // Moving downward
            } else if (adjustedPlacement.includes(PLACEMENT_TOP)) {
              return mouseY >= dropdownRect.bottom - 5 // Moving upward
            }

            return false
          }

          if (!isMovingToward()) {
            handleOpenChange(false, 'trigger')
          }
        } else {
          handleOpenChange(false, 'trigger')
        }
      }
    }
    if (trigger.includes('contextMenu')) {
      props.onContextMenu = e => {
        e.preventDefault()
        handleOpenChange(true, 'trigger')
      }
    }

    return props
  }

  const getMenuProps = () => {
    const props: React.HTMLAttributes<HTMLDivElement> = {}

    if (trigger.includes('hover')) {
      props.onMouseEnter = () => {
        // Keep dropdown open when mouse enters the menu
      }
      props.onMouseLeave = () => {
        handleOpenChange(false, 'menu')
      }
    }

    return props
  }

  const renderMenu = () => {
    const menuContent = (
      <ul className={classNames(styles.menu, menu.className)}>
        {menu.items.map(item => (
          <li
            key={item.key}
            className={classNames(styles.menuItem, item.disabled ? styles.disabled : null, menuItemClassName)}
            onClick={e => {
              handleMenuItemClick(e, item)
            }}
          >
            {item.icon && <span className={styles.menuItemIcon}>{item.icon}</span>}
            <span className={styles.menuItemLabel}>{item.label}</span>
          </li>
        ))}
      </ul>
    )

    return dropdownRender ? dropdownRender(menuContent) : menuContent
  }

  // Update the getLabelSize function
  const getLabelSize = () => {
    // Check if children is a string
    if (typeof children === 'string') {
      return children.length > 8 ? 'largeLabel' : 'smallLabel'
    }

    // If children is a React element with a text content
    if (isValidElement(children)) {
      // Handle direct string children
      if (typeof children.props.children === 'string') {
        return children.props.children.length > 8 ? 'largeLabel' : 'smallLabel'
      }

      // Try to extract text from more complex children
      try {
        const text = Children.toArray(children.props.children)
          .filter(child => typeof child === 'string')
          .join('')

        return text.length > 8 ? 'largeLabel' : 'smallLabel'
      } catch (e) {
        // If we can't extract text, default to large
        return 'largeLabel'
      }
    }

    // Default to large label if we can't determine
    return 'largeLabel'
  }

  const renderTrigger = () => {
    const labelSize = getLabelSize()

    return (
      <div
        ref={triggerRef}
        className={classNames(
          styles.trigger,
          buttonClassName,
          open || isActive ? styles.active : null,
          open ? styles.open : null,
        )}
        data-label-size={labelSize}
        {...getTriggerProps()}
      >
        <span className={styles.dropdownLabel}>{children}</span>
        {showArrow && <span className={styles.arrow}>{customArrow ?? <Icon iconSvg={Arrow} />}</span>}
      </div>
    )
  }

  if (disabled) {
    return <div className={classNames(styles.disabledWrapper, className)}>{children}</div>
  }

  return (
    <div className={classNames(styles.dropdown, className)}>
      {renderTrigger()}
      {(open || !destroyPopupOnHide) && (
        <div
          ref={dropdownRef}
          className={classNames(styles.overlay, styles[adjustedPlacement], overlayClassName, open ? styles.open : null)}
          {...getMenuProps()}
        >
          {renderMenu()}
        </div>
      )}
    </div>
  )
}
